import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { useCreateStoryboard } from "@/hooks/storyboards/useCreateStoryboard";
import { useDuplicateStoryboard } from "@/hooks/storyboards/useDuplicateStoryboard";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import { fetchFileTree } from "@/store/codebaseSlice";
import { fetchGitStatus } from "@/store/gitStatusSlice";
import {
  selectHoveredTempoElement,
  selectMultiSelectedTempoElements,
  selectSelectedTempoElement,
} from "@/store/iframeSlice";
import {
  useCanvasStore,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/store/zustand/canvasStore";
import { useChangeLedgerStore } from "@/store/zustand/changeLedger";
import {
  AddJsxChange,
  AnyChangeLedgerItem,
  DuplicateChange,
} from "@/store/zustand/changeLedgerTypes";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore, { LayoutMode } from "@/store/zustand/layoutStore";
import { atob_utf8 } from "@/utils/bytes";
import {
  getComponentFileFromExtractedElements,
  getExtractedElement,
} from "@/utils/extractElementsUtils";
import { ReactFramework } from "@/utils/templateProjects";
import { TempoElement } from "@/utils/tempoElement";
import { FileData } from "@/utils/types";
import {
  getUnexpiredToken,
  imageStoryboardTemplateNextJs,
  imageStoryboardTemplateReact,
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import * as parser from "@babel/parser";
import axios from "axios";
import React, { useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { useSession } from "@clerk/clerk-react";
import { useReactComponentHandler } from "@/hooks/useReactComponentHandler";
import { useStartServer } from "@/utils/devServerUtils";
import { SignedInSessionResource } from "@clerk/types";

export const TEMPO_ELEMENT_PASTE_STORYBOARD_WIDTH = 800;
export const TEMPO_ELEMENT_PASTE_STORYBOARD_HEIGHT = 600;

enum CopyType {
  Element,
  Storyboard,
}

const validateCode = async (code) => {
  try {
    const ast = parser.parse(code, {
      sourceType: "module",
      plugins: ["jsx", "typescript"],
    });
    return true;
  } catch (error) {
    console.error("Invalid JSX:", error.message);
    return false;
  }
};

const createImageStoryboardContents = (
  pathToImage: string,
  altText: string,
  naturalWidth?: number,
  naturalHeight?: number,
  framework?: ReactFramework
) => {
  if (pathToImage.startsWith("public")) {
    pathToImage = pathToImage.replace("public", "");
  }

  return framework === ReactFramework.NEXT_JS ||
    framework === ReactFramework.NEXT_JS_APP_ROUTER
    ? imageStoryboardTemplateNextJs(
        pathToImage,
        // @ts-expect-error TS2345
        naturalWidth,
        naturalHeight,
        altText
      )
    : imageStoryboardTemplateReact(
        pathToImage,
        // @ts-expect-error TS2345
        naturalWidth,
        naturalHeight,
        altText
      );
};

export const useCreateImageStoryboard = () => {
  const initializeStoryboard = useInitializeStoryboard();
  const createStoryboard = useCreateStoryboard();
  const project = useActiveProject();

  return async ({
    filePath,
    positionX,
    positionY,
    actionText,
    width,
    height,
    altText,
  }: {
    filePath: string;
    positionX: number;
    positionY: number;
    width: number;
    height: number;
    altText: string;
    actionText: string;
  }) => {
    const newStoryboard = initializeStoryboard(
      positionX,
      positionY,
      width,
      height
    );

    await createStoryboard({
      storyboard: newStoryboard,
      fileContentsToCreateWith: createImageStoryboardContents(
        filePath,
        altText,
        width,
        height,
        project?.framework
      ),
      actionText,
    });
  };
};

const useHandleImagePaste = () => {
  const canvasId = useCanvasId();
  const dispatch = useDispatch<any>();
  const project = useActiveProject();
  const canvas = useActiveCanvas();
  const restartServer = useStartServer();

  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse
  );

  const { session } = useSession();

  const initializeStoryboard = useInitializeStoryboard();
  const createStoryboard = useCreateStoryboard();

  const { triggerRefetchActivityStream } = useSupabase();

  /**
   * Takes an image item from the clipboard, creates a new file in an assets directory, then creates a storyboard with the a image rendered with the image.
   * @param blob
   * @param options - Options for the image paste.
   */
  const handleImagePaste = async (
    blob,
    options: {
      position?: { x: number; y: number };
    } = {}
  ) => {
    if (!session) {
      return;
    }

    try {
      const mimeType = blob.type;
      const extension = mimeType.split("/")[1]; // Extracts file extension from MIME type

      // Create a timestamp-based file name
      const timestamp = new Date().toISOString().replace(/[-:.]/g, ""); // ISO string with no special characters
      const assetDir = "public/images/";
      const basename = `tempo-image-${timestamp}.${extension}`;
      const fileName = `${assetDir}${basename}`;

      const file = new File([blob], basename, { type: mimeType });

      let formData = new FormData();
      formData.append("file", file as File);
      formData.append("directoryToUploadTo", assetDir);

      const supabaseJWT = await getUnexpiredToken(session);

      const res = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/createFile`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res?.data) {
        updateLookupsBasedOnPartialResponse(res.data);
        triggerRefetchActivityStream();
      }

      dispatch(
        fetchGitStatus({
          session,
          canvasId,
        })
      );

      dispatch(
        fetchFileTree({
          session,
          canvasId,
        })
      );

      toast.success("Uploaded file");

      const { x, y } = useCanvasStore.getState().canvasMousePos;

      let positionX = x;
      let positionY = y;

      if (options.position) {
        positionX = options.position.x;
        positionY = options.position.y;
      }

      try {
        const tempUrl = URL.createObjectURL(blob);

        // Create a new Image object to load the blob
        const img = new Image();
        img.onload = async () => {
          // Get the dimensions of the image
          const width = img.naturalWidth;
          const height = img.naturalHeight;

          try {
            const newStoryboard = initializeStoryboard(
              positionX,
              positionY,
              width,
              height
            );

            await createStoryboard({
              storyboard: newStoryboard,
              fileContentsToCreateWith: createImageStoryboardContents(
                fileName,
                "Pasted Image",
                width,
                height,
                project?.framework
              ),
              actionText: "Pasted Image",
            });

            await restartServer({ forceRestart: true });
          } catch (error) {
            toast.error("Couldn't paste image: ", error);
          }

          // Release the temporary URL
          URL.revokeObjectURL(tempUrl);
        };
        // Set the src of the image to the temporary URL to start loading
        img.src = tempUrl;
      } catch (error) {
        toast.error("Couldn't paste image: ", error);
      }
    } catch (error) {
      console.error("Error processing pasted image:", error);
      toast.error("Error processing image: " + error.message);
      throw error;
    }
  };

  return handleImagePaste;
};

/**
 * Component used to handle side-effects that should not re-render the app
 * but are related to the hook.
 *
 * Note, if the hook is called in multiple places this component will be initialized in
 * multiple places as well
 */
const SideEffectComponent: React.FC = React.memo(() => {
  const canvasId = useCanvasId();

  const { session } = useSession();

  const { supabaseClient } = useSupabase();
  const fileTree = useSelector((state: any) => state.codebase.fileTree);

  const filePathToFileTreeLookup = useMemo(() => {
    let filePathToFileTreeLookup = {};

    const traverseTree = (file: FileData) => {
      if (file.type === "file") {
        filePathToFileTreeLookup[file.fullPath] = file;
      } else if (file.items) {
        file.items.forEach((child) => {
          traverseTree(child);
        });
      }
    };

    if (fileTree) {
      fileTree.forEach((file) => {
        traverseTree(file);
      });
    }

    return filePathToFileTreeLookup;
  }, [fileTree]);

  const elementKeyToNavNodeLookup = useCanvasStore(
    (store) => store.elementKeyToNavNodeLookup
  );

  const project = useActiveProject();
  const canvas = useActiveCanvas();

  const basePathToProject = useSelector(
    (state: any) => state.codebase.basePathToProject
  );

  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );

  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);
  const hoveredElement: TempoElement = useSelector(selectHoveredTempoElement);
  const multiselectedElements: TempoElement[] = useSelector(
    selectMultiSelectedTempoElements
  );

  const layoutMode = useLayoutStore((store) => store.layoutMode);

  const selectedNavTreeNode = useSelectedNavTreeNode();
  const multiselectedNavTreeNodes = useMultiselectedNavTreeNode();

  const extractedComponentFile = useSelector(
    (state: any) => state.iframe.extractedComponentFile
  );
  const extractedComponentFilePath = useSelector(
    (state: any) => state.iframe.extractedComponentFilePath
  );

  const selectedElementHTML = useSelector(
    (state: any) => state.iframe.selectedElementHTML
  );
  const multiselectedElementHTML = useSelector(
    (state: any) => state.iframe.multiselectedElementHTML
  );

  const addStoryboardIdToSetHTML = useCanvasStore(
    (store) => store.addStoryboardIdToSetHTML
  );
  const addChange = useChangeLedgerStore((store) => store.addChange);

  const duplicateStoryboard = useDuplicateStoryboard();

  const initializeStoryboard = useInitializeStoryboard();
  const createStoryboard = useCreateStoryboard();

  const handleImagePaste = useHandleImagePaste();

  const { installDepsForComponent } = useReactComponentHandler();

  const handleTempoCopyElementPaste = async (tempoCopyStr: string) => {
    // Try to parse the text as JSON
    const parsed = JSON.parse(tempoCopyStr);
    if (!parsed) {
      return;
    }

    const supabaseJWT = await getUnexpiredToken(session);

    if (parsed.type === CopyType.Element) {
      const {
        fileContents,
        filePath,
        selectedElementKey: copiedSelectedElementKey,
        multiselectedElementKeys: copiedMultiselectedElementKeys,
        html,
        width,
        height,
      } = parsed;

      let fileContentsToUse = fileContents;
      let filePathToUse = filePath;

      // If they haven't been loaded yet, make an API request to load them
      if (!fileContents || !filePath) {
        let copiedNavTreeNodes = [];
        if (copiedMultiselectedElementKeys?.length) {
          copiedNavTreeNodes = copiedMultiselectedElementKeys.map(
            (key) => elementKeyToNavNodeLookup[key]
          );
        } else {
          copiedNavTreeNodes = [
            // @ts-expect-error TS2322
            elementKeyToNavNodeLookup[copiedSelectedElementKey],
          ];
        }

        if (
          !copiedNavTreeNodes?.length ||
          copiedNavTreeNodes.filter((n) => n).length !==
            copiedNavTreeNodes.length
        ) {
          toast.error(
            "Copied elements cannot be found. Please re-select the elements to copy and try again."
          );
          return;
        }

        const extractedElements = [];
        let error;
        copiedNavTreeNodes.forEach((navTreeNode) => {
          const { extractedElement, error: _error } = getExtractedElement(
            navTreeNode,
            treeElementLookup
          );
          if (error) {
            error = _error;
            return;
          }

          // @ts-expect-error TS2345
          extractedElements.push(extractedElement);
        });

        if (!extractedElements?.length) {
          toast.error("Elements could not be found to be copied");
          return;
        }

        if (error) {
          toast.error("Error copying elements: " + error);
          return;
        }

        try {
          const { newFileContents, dummyFilePath } =
            await getComponentFileFromExtractedElements(
              canvasId,
              // @ts-expect-error TS2345
              supabaseJWT,
              extractedElements
            );

          fileContentsToUse = newFileContents;
          filePathToUse = dummyFilePath;
        } catch (error) {
          toast.error("Error copying elements: " + error);
          return;
        }
      }

      if (selectedElement.isKnownElement()) {
        // Pasting into a storyboard
        const navTreeNode = elementKeyToNavNodeLookup[selectedElement.getKey()];
        const codebaseIdToAddTo = navTreeNode.tempoElement.codebaseId;

        // If copy/pasting into the same element, duplicate instead
        // Logic is if there is any overlap between the multiselected/selected elements
        const copiedElementKeys = copiedMultiselectedElementKeys || [
          copiedSelectedElementKey,
        ];
        const currentElementKeys = multiselectedElements?.length
          ? multiselectedElements.map((element) => element.getKey())
          : [selectedElement.getKey()];

        // Check if there is any overlap
        const overlap = copiedElementKeys.some((key) =>
          currentElementKeys.includes(key)
        );

        let change: AnyChangeLedgerItem;
        if (overlap) {
          change = new DuplicateChange({
            codebaseIdsToDuplicate: copiedElementKeys.map(
              (key) => elementKeyToNavNodeLookup[key].tempoElement.codebaseId
            ),
          });
        } else {
          change = new AddJsxChange({
            codebaseIdToAddTo: codebaseIdToAddTo,
            fileContentsToSourceFrom: fileContentsToUse,
            fileContentsSourceFilename: filePathToUse,
            htmlForInstantUpdate: html,
          });
        }

        addChange(change);
        return;
      }

      const { x, y } = useCanvasStore.getState().canvasMousePos;

      const left = x - (width ? width / 2 : 0);
      const top = y - (height ? height / 2 : 0);

      const newStoryboard = initializeStoryboard(
        left,
        top,
        width || TEMPO_ELEMENT_PASTE_STORYBOARD_WIDTH,
        height || TEMPO_ELEMENT_PASTE_STORYBOARD_HEIGHT
      );

      createStoryboard({
        storyboard: newStoryboard,
        fileContentsToCreateWith: fileContentsToUse,
        // @ts-expect-error TS2345
        filePathToUse,
        actionText: "Pasted Tempo Component",
      });

      addStoryboardIdToSetHTML(newStoryboard.id, html);
      return;
    } else if (parsed.type === CopyType.Storyboard) {
      const { storyboardId } = parsed;
      // @ts-expect-error TS18047
      const storyboardToPaste = canvasRef.current.storyboards.find(
        (s) => s.id === storyboardId
      );

      const canvasMousePos = useCanvasStore.getState().canvasMousePos;

      // @ts-expect-error TS2345
      duplicateStoryboard(storyboardToPaste, canvasMousePos);
    }
  };

  const handlePaste = async (e: ClipboardEvent) => {
    let metadata;

    // Check if we're pasting into an input context
    const target = e.target as HTMLElement;
    // skip handling paste if the target is an input, textarea, code, or part of a Monaco editor or PRD text editor (aka normal paste)
    if (
      // Check if element is explicitly contentEditable
      target.contentEditable === "true" ||
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.tagName === "CODE" ||
      target.closest('[data-generate-input="true"]') || // ai chat
      target.closest(".monaco-editor") || // source code editor
      target.closest("#tiptap-editor") || // PRD text editor
      target.closest(".ProseMirror-trailingBreak") // PRD text editor when pasting images
    ) {
      return;
    }

    // No pasting in PRD mode
    if (layoutMode === LayoutMode.PRD) {
      toast.error("Pasting is disabled in PRD mode");
      return;
    }

    if (!canvasRef.current) {
      return;
    }

    // Create a loading storyboard at the mouse position
    const { x, y } = useCanvasStore.getState().canvasMousePos;
    useCanvasStore.getState().setLoadingStoryboard({
      id: "loading-" + Date.now(),
      x: x - 100, // Center the loading storyboard
      y: y - 100,
      width: 200,
      height: 200,
    });

    // @ts-ignore
    const clipboardData = e.clipboardData || window.clipboardData;

    // Look for an image type in the clipboard items
    let imageBlob = null;
    let tempoCopyBlob = clipboardData.getData("web tempo/copyelement");
    for (let i = 0; i < clipboardData.items.length; i++) {
      const item = clipboardData.items[i];
      // Check if any of the types are of an image
      if (item.type.startsWith("image/")) {
        imageBlob = item.getAsFile();
        break;
      }
    }
    let nonShadcnFiles: { [key: string]: string } = {};

    // Check if the clipboard data contains tempo-metadata
    const htmlData = e.clipboardData?.getData("text/html");
    try {
      if (htmlData) {
        const match = htmlData.match(/<!--tempo-metadata:(.*?)-->/);
        if (match) {
          const parsedMetadata = JSON.parse(atob_utf8(match[1]));
          metadata = parsedMetadata;

          // Process component metadata using the new hook
          const { success, nonShadcnFiles: newNonShadcnFiles } =
            await installDepsForComponent(metadata);
          nonShadcnFiles = { ...nonShadcnFiles, ...newNonShadcnFiles };

          if (!success) {
            useCanvasStore.getState().setLoadingStoryboard(null); // Remove loading storyboard if operation fails
            return; // Abort the entire paste operation
          }
        }
      }
    } catch (error) {
      console.error("Couldn't handle metadata paste:" + error);
      useCanvasStore.getState().setLoadingStoryboard(null); // Remove loading storyboard if operation fails
    }

    if (imageBlob) {
      try {
        await handleImagePaste(imageBlob);
      } catch (error) {
        console.error("Couldn't handle image paste:" + error);
        useCanvasStore.getState().setLoadingStoryboard(null); // Remove loading storyboard if operation fails
        return;
      }
    }

    if (tempoCopyBlob) {
      try {
        await handleTempoCopyElementPaste(tempoCopyBlob);
      } catch (error) {
        console.error("Couldn't handle copying tempo element:" + error);
        useCanvasStore.getState().setLoadingStoryboard(null); // Remove loading storyboard if operation fails
        return;
      }
    }

    const text = await getPastedText();

    if (text === "") {
      useCanvasStore.getState().setLoadingStoryboard(null); // Remove loading storyboard if no text to paste
      return;
    }

    const isValid = await validateCode(text);
    const { x: mouseX, y: mouseY } = useCanvasStore.getState().canvasMousePos;

    if (isValid) {
      try {
        const newStoryboard = initializeStoryboard(mouseX, mouseY, 1200, 800);

        await createStoryboard({
          storyboard: newStoryboard,
          fileContentsToCreateWith: text,
          actionText: "Pasted Code",
          extraPathToContents: nonShadcnFiles,
        });
      } catch (error) {
        toast.error("Couldn't paste: ", error);
      } finally {
        useCanvasStore.getState().setLoadingStoryboard(null); // Remove loading storyboard when operation completes
      }
    } else {
      toast.warning("Clipboard contents not valid");
      useCanvasStore.getState().setLoadingStoryboard(null); // Remove loading storyboard if content is invalid
    }
  };

  const getPastedText = async () => {
    try {
      const clipboardData = await navigator.clipboard.readText();
      return clipboardData;
    } catch (error) {
      console.error("Error reading clipboard data:", error);
      return "";
    }
  };

  /**
   * Register a paste handler and set ALL non-ref dependencies to any variables used within the handler.
   */
  useEffect(() => {
    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [
    selectedElement,
    hoveredElement,
    project,
    Boolean(session),
    Boolean(supabaseClient),
    elementKeyToNavNodeLookup,
    treeElementLookup,
    layoutMode,
  ]);

  const handleCopy = (e) => {
    if (e.target.closest(".monaco-editor")) {
      return;
    }

    if (!canvasRef.current) {
      return;
    }

    let copyData = null;

    if (selectedElement.isKnownElement()) {
      // @ts-expect-error TS18047
      let boundingBox = selectedNavTreeNode.pageBoundingBox;

      if (multiselectedNavTreeNodes) {
        multiselectedNavTreeNodes.forEach((node) => {
          const boundingBoxToAdd = node.pageBoundingBox;
          if (boundingBoxToAdd) {
            // @ts-expect-error TS18048
            boundingBox.pageX = Math.min(
              // @ts-expect-error TS18048
              boundingBox.pageX,
              boundingBoxToAdd.pageX
            );
            // @ts-expect-error TS18048
            boundingBox.pageY = Math.min(
              // @ts-expect-error TS18048
              boundingBox.pageY,
              boundingBoxToAdd.pageY
            );

            // @ts-expect-error TS18048
            const prevRight = boundingBox.pageX + boundingBox.width;
            const newRight = boundingBoxToAdd.pageX + boundingBoxToAdd.width;
            // @ts-expect-error TS18048
            boundingBox.width =
              // @ts-expect-error TS18048
              Math.max(prevRight, newRight) - boundingBox.pageX;

            // @ts-expect-error TS18048
            const prevBottom = boundingBox.pageY + boundingBox.height;
            const newBottom = boundingBoxToAdd.pageY + boundingBoxToAdd.height;
            // @ts-expect-error TS18048
            boundingBox.height =
              // @ts-expect-error TS18048
              Math.max(prevBottom, newBottom) - boundingBox.pageY;
          }
        });
      }
      // @ts-expect-error TS2322
      copyData = {
        type: CopyType.Element,
        selectedElementKey: selectedElement.getKey(),
        multiselectedElementKeys: multiselectedElements?.map((element) =>
          element.getKey()
        ),
        fileContents: extractedComponentFile,
        filePath: extractedComponentFilePath,
        html: multiselectedElementHTML || selectedElementHTML,
        width: boundingBox?.width,
        height: boundingBox?.height,
      };
    } else if (selectedElement.isStoryboard()) {
      // @ts-expect-error TS2322
      copyData = {
        type: CopyType.Storyboard,
        storyboardId: selectedElement.storyboardId,
      };
    }

    if (!copyData) {
      return;
    }

    // @ts-ignore
    const clipboardData = e.clipboardData || window.clipboardData;

    clipboardData.setData("web tempo/copyelement", JSON.stringify(copyData));
    clipboardData.setData("text/plain", window.getSelection()?.toString());

    e.preventDefault();
  };

  useEffect(() => {
    window.addEventListener("copy", handleCopy);

    return () => {
      window.removeEventListener("copy", handleCopy);
    };
  }, [
    selectedElement,
    multiselectedElements,
    selectedElementHTML,
    multiselectedElementHTML,
    selectedNavTreeNode,
    multiselectedNavTreeNodes,
    extractedComponentFile,
    extractedComponentFilePath,
  ]);

  return <></>;
});

export const useCopyPaste = () => {
  const handleImagePaste = useHandleImagePaste();

  return {
    handleImagePaste,
    SideEffectComponentInstance: <SideEffectComponent />,
  };
};
